/**
 * Disables page scrolling, typically used when opening modal windows.
 * - Saves the scrollbar width in the custom property `--scrollbar-width` to prevent content from shifting.
 * - Sets the `overflow: hidden` style on body to block scrolling.
 * - Adds the `modal-open` class to apply the appropriate styles.
 */
export function disableScroll() {
  const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.body.style.setProperty('--scrollbar-width', `${scrollBarWidth}px`);
  document.body.style.overflow = 'hidden';
  document.body.classList.add('modal-open');
}

/**
 * Enables page scrolling, typically used when closing modal windows.
 * - Removes the `modal-open` class from body, restoring standard scrolling behavior.
 * - Removes the custom property `--scrollbar-width` that was set when disabling scrolling.
 * - Restores the `overflow: auto` style, allowing page scrolling.
 */
export function enableScroll() {
  document.body.classList.remove('modal-open');
  document.body.style.removeProperty('--scrollbar-width');
  document.body.style.overflow = 'auto';
}

/**
 * Toggles the scrolling state depending on whether a modal window is open.
 * @param {boolean} isModalOpen - Flag indicating whether the modal window is open.
 * If `isModalOpen` is true, scrolling is disabled; if false, scrolling is enabled.
 */
export const toggleScroll = isModalOpen => (isModalOpen ? disableScroll() : enableScroll());

/**
 * Scrolls the page to the top.
 * This can be useful, for example, when navigating to another page or when opening a modal window.
 */
export const scrollToTop = () => {
  window.scrollTo(0, 0);
};

/**
 * Scrolls the page to the element with the specified id.
 * @param {string} elementId - ID of the element to scroll to.
 * @param {number} delay - Delay before scrolling in milliseconds.
 */
export const scrollToElement = (elementId, delay = 300) => {
  const savedPosition = localStorage.getItem('scrollTo');
  if (savedPosition === elementId) {
    setTimeout(() => {
      window.scrollTo({
        top: Number(document.getElementById(elementId)?.offsetTop ?? 0),
        behavior: 'smooth',
      });
    }, delay);
    localStorage.removeItem('scrollTo');
  }
};

/**
 * Adds a scroll event handler.
 * @param {Function} onScrollCallback - Callback function for handling the scroll event.
 */
export const addScrollListener = onScrollCallback => {
  window.addEventListener('scroll', onScrollCallback);
};

/**
 * Removes a scroll event handler.
 * @param {Function} onScrollCallback - Callback function to remove the handler.
 */
export const removeScrollListener = onScrollCallback => {
  window.removeEventListener('scroll', onScrollCallback);
};

/**
 * Saves the current scroll position and the top visible post for the current route
 * @param {string} routeName - Route name (e.g., 'home')
 */
export const saveScrollPosition = routeName => {
  const scrollPosition = window.scrollY;
  const scrollState = JSON.parse(localStorage.getItem('scrollState') || '{}');

  // Find the top visible post for more accurate position restoration
  const visiblePostId = findVisiblePostId();

  scrollState[routeName] = {
    position: scrollPosition,
    postId: visiblePostId,
    timestamp: Date.now(),
  };

  localStorage.setItem('scrollState', JSON.stringify(scrollState));
};

/**
 * Restores the saved scroll position for the current route
 * @param {string} routeName - Route name (e.g., 'home')
 * @param {number} delay - Delay in ms before restoring the position
 */
export const restoreScrollPosition = (routeName, delay = 300) => {
  setTimeout(() => {
    const scrollState = JSON.parse(localStorage.getItem('scrollState') || '{}');
    const savedState = scrollState[routeName];

    if (savedState) {
      // If there is a post ID, scroll to it, otherwise to the position
      if (savedState.postId && document.getElementById(savedState.postId)) {
        const postElement = document.getElementById(savedState.postId);
        window.scrollTo({
          top: postElement.offsetTop,
          behavior: 'auto',
        });
      } else if (savedState.position) {
        window.scrollTo({
          top: savedState.position,
          behavior: 'auto',
        });
      }

      // If more than 30 minutes have passed, delete the saved state
      const thirtyMinutesInMs = 30 * 60 * 1000;
      if (Date.now() - savedState.timestamp > thirtyMinutesInMs) {
        delete scrollState[routeName];
        localStorage.setItem('scrollState', JSON.stringify(scrollState));
      }
    }
  }, delay);
};

/**
 * Finds the ID of the top visible post in the feed
 * @returns {string|null} ID of the top visible post or null
 */
const findVisiblePostId = () => {
  const posts = Array.from(document.querySelectorAll('.post'));
  for (const post of posts) {
    const rect = post.getBoundingClientRect();
    // Consider a post visible if its top part is within the screen
    if (rect.top >= 0 && rect.top < window.innerHeight) {
      return post.id || null;
    }
  }
  return null;
};
